import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  ExclamationCircleIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  UserCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  createDoctor,
  deleteDoctor,
  updateDoctor,
  uploadDoctorSignature,
} from '../services/api/apis';
import { useDoctorList } from '../hooks/useDoctorList';
import type { Doctor } from '../types';

// ─── types ────────────────────────────────────────────────────────────────────

interface DoctorFormState {
  name: string;
  regNo: string;
  specialization: string;
  signatureFile: File | null;
  signaturePreviewUrl: string | null;
}

const emptyForm = (): DoctorFormState => ({
  name: '',
  regNo: '',
  specialization: '',
  signatureFile: null,
  signaturePreviewUrl: null,
});

// ─── sub-components ───────────────────────────────────────────────────────────

const inputCls =
  'w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400';

interface SignaturePickerProps {
  file: File | null;
  previewUrl: string | null;
  existingUrl?: string | null;
  onChange: (file: File | null, previewUrl: string | null) => void;
}

const SignaturePicker: React.FC<SignaturePickerProps> =({ file, previewUrl, existingUrl, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (f: File) => {
    const url = URL.createObjectURL(f);
    onChange(f, url);
  };

  const handleClear = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    onChange(null, null);
  };

  const displayed = previewUrl ?? existingUrl ?? null;

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        Signature
      </label>
      {displayed ? (
        <div className="relative inline-block">
          <img
            src={displayed}
            alt="Signature preview"
            className="h-20 w-auto max-w-xs rounded border border-gray-200 dark:border-gray-700 bg-white object-contain"
          />
          <button
            type="button"
            onClick={handleClear}
            className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
            title="Remove"
          >
            <XMarkIcon className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-400 hover:text-blue-500 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Upload signature image
        </button>
      )}
      {displayed && !previewUrl && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="text-xs text-blue-500 hover:underline"
        >
          Replace
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = '';
        }}
      />
    </div>
  );
};

// ─── modal ────────────────────────────────────────────────────────────────────

interface DoctorModalProps {
  editing: Doctor | null;
  onClose: () => void;
  onSaved: () => void;
}

const DoctorModal: React.FC<DoctorModalProps> = ({ editing, onClose, onSaved }) => {
  const [form, setForm] = useState<DoctorFormState>(
    editing
      ? {
          name: editing.name,
          regNo: editing.regNo,
          specialization: editing.specialization,
          signatureFile: null,
          signaturePreviewUrl: null,
        }
      : emptyForm()
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<{ field?: string; message: string } | null>(null);

  // revoke on unmount
  useEffect(
    () => () => {
      if (form.signaturePreviewUrl) URL.revokeObjectURL(form.signaturePreviewUrl);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const set = (patch: Partial<DoctorFormState>) => setForm((f) => ({ ...f, ...patch }));

  const isDirty = editing
    ? form.name !== editing.name ||
      form.regNo !== editing.regNo ||
      form.specialization !== editing.specialization ||
      form.signatureFile !== null
    : true;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) { setError({ field: 'name', message: 'Name is required' }); return; }
    if (!form.regNo.trim()) { setError({ field: 'regNo', message: 'Registration number is required' }); return; }
    if (!form.specialization.trim()) { setError({ field: 'specialization', message: 'Specialization is required' }); return; }

    setSaving(true);
    try {
      let signatureKey: string | undefined;
      if (form.signatureFile) {
        signatureKey = await uploadDoctorSignature(form.signatureFile, editing?.id);
      }

      if (editing) {
        const payload: Parameters<typeof updateDoctor>[1] = {
          name: form.name.trim(),
          regNo: form.regNo.trim(),
          specialization: form.specialization.trim(),
          ...(signatureKey ? { signatureKey } : {}),
        };
        await updateDoctor(editing.id, payload);
      } else {
        await createDoctor({
          name: form.name.trim(),
          regNo: form.regNo.trim(),
          specialization: form.specialization.trim(),
          ...(signatureKey ? { signatureKey } : {}),
        } as Parameters<typeof createDoctor>[0]);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      // After Axios interceptors, thrown errors are ApiError instances.
      // Use err.statusCode (not err.response.status) and err.details.originalMessage
      // for the actual backend error text.
      const msg: string =
        (err?.details?.originalMessage as string | undefined) ??
        err?.message ??
        'Something went wrong';
      const statusCode: number | undefined = err?.statusCode;
      if (statusCode === 409 || msg.toLowerCase().includes('already exists') || msg.toLowerCase().includes('conflict')) {
        setError({ field: 'regNo', message: 'A doctor with this registration number already exists.' });
      } else if (statusCode === 422 || msg.toLowerCase().includes('signature')) {
        setError({ message: 'Signature upload failed. Please try a different image.' });
      } else {
        setError({ message: msg });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800">
          <h2 className="text-base font-bold text-gray-900 dark:text-white">
            {editing ? 'Edit Doctor' : 'New Doctor'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="Dr. Jane Smith"
              className={`${inputCls} ${error?.field === 'name' ? 'border-red-400 focus:ring-red-400' : ''}`}
            />
            {error?.field === 'name' && (
              <p className="text-xs text-red-500">{error.message}</p>
            )}
          </div>

          {/* Reg No */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Registration number
            </label>
            <input
              type="text"
              value={form.regNo}
              onChange={(e) => set({ regNo: e.target.value })}
              placeholder="MCI-12345"
              className={`${inputCls} ${error?.field === 'regNo' ? 'border-red-400 focus:ring-red-400' : ''}`}
            />
            {error?.field === 'regNo' && (
              <p className="text-xs text-red-500">{error.message}</p>
            )}
          </div>

          {/* Specialization */}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Specialization
            </label>
            <input
              type="text"
              value={form.specialization}
              onChange={(e) => set({ specialization: e.target.value })}
              placeholder="General Physician"
              className={`${inputCls} ${error?.field === 'specialization' ? 'border-red-400 focus:ring-red-400' : ''}`}
            />
            {error?.field === 'specialization' && (
              <p className="text-xs text-red-500">{error.message}</p>
            )}
          </div>

          {/* Signature */}
          <SignaturePicker
            file={form.signatureFile}
            previewUrl={form.signaturePreviewUrl}
            existingUrl={editing?.signatureUrl ?? null}
            onChange={(file, previewUrl) => set({ signatureFile: file, signaturePreviewUrl: previewUrl })}
          />

          {/* Generic error */}
          {error && !error.field && (
            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
              <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
              {error.message}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !isDirty}
              className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm"
            >
              {saving ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                  Saving…
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── delete confirmation ──────────────────────────────────────────────────────

interface DeleteDialogProps {
  doctor: Doctor;
  onCancel: () => void;
  onConfirmed: () => void;
}

const DeleteDialog: React.FC<DeleteDialogProps> = ({ doctor, onCancel, onConfirmed }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await deleteDoctor(doctor.id);
      onConfirmed();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Delete failed');
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
        <h2 className="text-base font-bold text-gray-900 dark:text-white">Delete doctor?</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          <span className="font-semibold">{doctor.name}</span> will be permanently removed. This
          cannot be undone.
        </p>
        {error && (
          <p className="text-xs text-red-500 flex items-center gap-1">
            <ExclamationCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm"
          >
            {deleting ? (
              <>
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                Deleting…
              </>
            ) : (
              'Delete'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── doctor card ──────────────────────────────────────────────────────────────

interface DoctorCardProps {
  doctor: Doctor;
  onEdit: (d: Doctor) => void;
  onDelete: (d: Doctor) => void;
}

const DoctorCard: React.FC<DoctorCardProps> = ({ doctor, onEdit, onDelete }) => (
  <div className="flex items-center gap-4 px-5 py-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow transition-shadow">
    {/* Signature thumbnail or placeholder */}
    <div className="w-14 h-14 flex-shrink-0 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-50 dark:bg-gray-800 flex items-center justify-center">
      {doctor.signatureUrl ? (
        <img
          src={doctor.signatureUrl}
          alt={`${doctor.name} signature`}
          className="w-full h-full object-contain"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      ) : (
        <UserCircleIcon className="w-8 h-8 text-gray-300 dark:text-gray-600" />
      )}
    </div>

    {/* Info */}
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{doctor.name}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{doctor.specialization}</p>
      <p className="text-xs font-mono text-gray-400 dark:text-gray-500 mt-0.5">{doctor.regNo}</p>
    </div>

    {/* Actions */}
    <div className="flex items-center gap-1 flex-shrink-0">
      <button
        type="button"
        onClick={() => onEdit(doctor)}
        className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
        title="Edit"
      >
        <PencilSquareIcon className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => onDelete(doctor)}
        className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        title="Delete"
      >
        <TrashIcon className="w-4 h-4" />
      </button>
    </div>
  </div>
);

// ─── page ─────────────────────────────────────────────────────────────────────

export const AdminDoctorsPage: React.FC = () => {
  const { doctors, isLoading, error, refresh } = useDoctorList();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Doctor | null>(null);
  const [deleting, setDeleting] = useState<Doctor | null>(null);

  const openCreate = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (d: Doctor) => { setEditing(d); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditing(null); };

  const handleSaved = () => refresh();
  const handleDeleted = () => { setDeleting(null); refresh(); };

  return (
    <div className="space-y-6 pb-16">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Doctors</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Manage doctor profiles and signatures.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
        >
          <PlusIcon className="w-4 h-4" />
          New Doctor
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-8">
          <ArrowPathIcon className="w-4 h-4 animate-spin" />
          Loading doctors…
        </div>
      )}

      {/* Error */}
      {!isLoading && error && (
        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
          <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
          {error}
          <button
            type="button"
            onClick={refresh}
            className="ml-2 underline hover:no-underline text-xs"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty */}
      {!isLoading && !error && doctors.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <UserCircleIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No doctors yet</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Click "New Doctor" to add the first one.
          </p>
        </div>
      )}

      {/* List */}
      {!isLoading && doctors.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {doctors.map((d) => (
            <DoctorCard key={d.id} doctor={d} onEdit={openEdit} onDelete={setDeleting} />
          ))}
        </div>
      )}

      {/* Create / edit modal */}
      {modalOpen && (
        <DoctorModal editing={editing} onClose={closeModal} onSaved={handleSaved} />
      )}

      {/* Delete confirmation */}
      {deleting && (
        <DeleteDialog doctor={deleting} onCancel={() => setDeleting(null)} onConfirmed={handleDeleted} />
      )}
    </div>
  );
};

export default AdminDoctorsPage;
